import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface SearchDoc {
  id: number;
  t: string; // page title
  p: string; // page path
  h: string; // heading
  a: string; // anchor
  b: string; // body
}

interface SearchResult {
  url: string;
  title: string;
  excerpt: string;
}

export interface SearchDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog should close */
  onClose: () => void;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function generateExcerpt(body: string, query: string): string {
  if (!body) return '';

  const terms = query
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 1);

  if (terms.length === 0) {
    const trimmed = body.slice(0, 150);
    return escapeHtml(trimmed) + (body.length > 150 ? '&hellip;' : '');
  }

  // Find best match position
  const lower = body.toLowerCase();
  let bestPos = -1;
  for (const term of terms) {
    const pos = lower.indexOf(term);
    if (pos !== -1) {
      bestPos = pos;
      break;
    }
  }
  if (bestPos === -1) bestPos = 0;

  // Extract window around match, aligned to word boundaries
  const radius = 60;
  let start = Math.max(0, bestPos - radius);
  let end = Math.min(body.length, bestPos + radius);
  if (start > 0) {
    const sp = body.indexOf(' ', start);
    if (sp !== -1 && sp < start + 15) start = sp + 1;
  }
  if (end < body.length) {
    const sp = body.lastIndexOf(' ', end);
    if (sp > end - 15) end = sp;
  }

  let excerpt = body.slice(start, end);
  if (start > 0) excerpt = '\u2026' + excerpt;
  if (end < body.length) excerpt += '\u2026';

  // Escape HTML then highlight terms
  excerpt = escapeHtml(excerpt);
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = terms.map((t) => esc(t)).join('|');
  excerpt = excerpt.replace(
    new RegExp(`(${pattern})`, 'gi'),
    '<mark>$1</mark>'
  );

  return excerpt;
}

const PAGE_SIZE = 15;

export function SearchDialog({ open, onClose }: SearchDialogProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [indexReady, setIndexReady] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const indexRef = useRef<any>(null);
  const dataRef = useRef<SearchDoc[]>([]);
  // All ranked IDs from the last search (for progressive loading)
  const rankedRef = useRef<number[]>([]);
  const queryRef = useRef('');
  const shownCountRef = useRef(0);

  // Load search data and build FlexSearch index on first open
  useEffect(() => {
    if (!open || indexReady) return;

    (async () => {
      try {
        const res = await fetch('/_search/search-data.json');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const docs: SearchDoc[] = await res.json();
        dataRef.current = docs;

        // Dynamic import keeps FlexSearch out of the main bundle
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const FlexSearch: any = await import('flexsearch');
        const DocumentClass =
          FlexSearch.Document || FlexSearch.default?.Document;
        const idx = new DocumentClass({
          document: {
            id: 'id',
            index: [
              { field: 't', tokenize: 'forward', resolution: 9 },
              { field: 'h', tokenize: 'forward', resolution: 9 },
              {
                field: 'b',
                tokenize: 'forward',
                resolution: 5,
                context: { depth: 2, resolution: 3, bidirectional: true },
              },
            ],
          },
        });

        for (const doc of docs) {
          idx.add(doc);
        }

        indexRef.current = idx;
        setIndexReady(true);
      } catch {
        console.warn('Search index not found. Run a build to generate it.');
      }
    })();
  }, [open, indexReady]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setQuery('');
      setResults([]);
      setActiveIndex(0);
      rankedRef.current = [];
      shownCountRef.current = 0;
    }
  }, [open]);

  // Convert ranked IDs to SearchResult items
  const buildResults = useCallback(
    (ids: number[], q: string): SearchResult[] => {
      const data = dataRef.current;
      return ids.map((id) => {
        const doc = data[id];
        const url = doc.a ? `${doc.p}#${doc.a}` : doc.p;
        const title = doc.h ? `${doc.t} \u203A ${doc.h}` : doc.t;
        const excerpt = generateExcerpt(doc.b, q);
        return { url, title, excerpt };
      });
    },
    []
  );

  // Load next page of results
  const loadMore = useCallback(() => {
    const ranked = rankedRef.current;
    const shown = shownCountRef.current;
    if (shown >= ranked.length) return;

    const nextBatch = ranked.slice(shown, shown + PAGE_SIZE);
    const items = buildResults(nextBatch, queryRef.current);
    shownCountRef.current = shown + nextBatch.length;
    setResults((prev) => [...prev, ...items]);
  }, [buildResults]);

  // Debounced search
  const doSearch = useCallback(
    (q: string) => {
      if (!indexRef.current || !q.trim()) {
        rankedRef.current = [];
        shownCountRef.current = 0;
        setResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const fieldResults: Array<{ field: string; result: number[] }> =
          indexRef.current.search(q, { limit: 100 });

        // Score by field: title match = 3, heading = 2, body = 1
        const scores = new Map<number, number>();
        for (const fr of fieldResults) {
          const weight = fr.field === 't' ? 3 : fr.field === 'h' ? 2 : 1;
          for (const id of fr.result) {
            scores.set(id, (scores.get(id) || 0) + weight);
          }
        }

        // Sort by score descending, store all ranked IDs
        const ranked = [...scores.entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([id]) => id);
        rankedRef.current = ranked;
        queryRef.current = q;

        // Render first page
        const firstPage = ranked.slice(0, PAGE_SIZE);
        shownCountRef.current = firstPage.length;
        setResults(buildResults(firstPage, q));
        setActiveIndex(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [indexReady, buildResults] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { root: resultsRef.current, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, results.length]);

  const handleInput = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) {
      rankedRef.current = [];
      shownCountRef.current = 0;
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => doSearch(value), 150);
  };

  const navigateToResult = (result: SearchResult) => {
    onClose();
    const hashIndex = result.url.indexOf('#');
    const path = hashIndex !== -1 ? result.url.slice(0, hashIndex) : result.url;
    const hash = hashIndex !== -1 ? result.url.slice(hashIndex + 1) : '';
    navigate(path);
    if (hash) {
      // Wait for the page to render markdown content, then scroll to anchor
      const scrollToAnchor = (attempts = 0) => {
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        } else if (attempts < 10) {
          setTimeout(() => scrollToAnchor(attempts + 1), 100);
        }
      };
      requestAnimationFrame(() => scrollToAnchor());
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (results[activeIndex]) {
          navigateToResult(results[activeIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  // Scroll active result into view
  useEffect(() => {
    if (!resultsRef.current) return;
    const active = resultsRef.current.querySelector('[data-active="true"]');
    if (active) {
      active.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-900/50 dark:bg-black/60" />

      {/* Dialog */}
      <div
        className="relative mx-4 flex w-full max-w-xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Search input */}
        <div className="flex items-center border-b border-gray-200 px-4 dark:border-gray-700">
          <svg
            className="mr-3 h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleInput(e.target.value)}
            placeholder="Search docs..."
            className="h-12 w-full bg-transparent text-sm text-gray-900 placeholder-gray-400 outline-none dark:text-gray-100 dark:placeholder-gray-500"
          />
          {query && (
            <button
              onClick={() => handleInput('')}
              className="ml-2 shrink-0 rounded p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
          <kbd className="ml-2 hidden shrink-0 rounded border border-gray-200 bg-gray-100 px-1.5 py-0.5 text-xs text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-500 sm:inline-block">
            esc
          </kbd>
        </div>

        {/* Results */}
        <div
          ref={resultsRef}
          className="max-h-[60vh] overflow-y-auto overscroll-contain"
        >
          {loading && results.length === 0 && query && (
            <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Searching...
            </div>
          )}

          {!loading && query && results.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No results for &ldquo;{query}&rdquo;
            </div>
          )}

          {!query && !indexReady && (
            <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Search index not available. Run a build to generate it.
            </div>
          )}

          {results.length > 0 && (
            <ul className="py-2">
              {results.map((result, i) => (
                <li key={`${result.url}-${i}`}>
                  <button
                    data-active={i === activeIndex}
                    onClick={() => navigateToResult(result)}
                    onMouseEnter={() => setActiveIndex(i)}
                    className={`flex w-full cursor-pointer flex-col gap-0.5 px-4 py-2.5 text-left transition-colors ${
                      i === activeIndex
                        ? 'bg-docs-50 dark:bg-docs-900/30'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    }`}
                  >
                    <span
                      className={`text-sm font-medium ${
                        i === activeIndex
                          ? 'text-docs-700 dark:text-docs-300'
                          : 'text-gray-900 dark:text-gray-100'
                      }`}
                    >
                      {result.title}
                    </span>
                    <span
                      className="line-clamp-2 text-xs text-gray-500 dark:text-gray-400 [&_mark]:bg-yellow-200/60 [&_mark]:text-gray-900 dark:[&_mark]:bg-yellow-500/30 dark:[&_mark]:text-gray-100"
                      dangerouslySetInnerHTML={{ __html: result.excerpt }}
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {/* Sentinel for infinite scroll */}
          {results.length > 0 && results.length < rankedRef.current.length && (
            <div ref={sentinelRef} className="h-1" />
          )}
        </div>

        {/* Footer */}
        {results.length > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-2 dark:border-gray-700">
            <div className="flex gap-2 text-xs text-gray-400 dark:text-gray-500">
              <span>
                <kbd className="rounded border border-gray-200 bg-gray-100 px-1 py-0.5 font-mono dark:border-gray-600 dark:bg-gray-800">
                  &uarr;
                </kbd>
                <kbd className="ml-0.5 rounded border border-gray-200 bg-gray-100 px-1 py-0.5 font-mono dark:border-gray-600 dark:bg-gray-800">
                  &darr;
                </kbd>{' '}
                navigate
              </span>
              <span>
                <kbd className="rounded border border-gray-200 bg-gray-100 px-1 py-0.5 font-mono dark:border-gray-600 dark:bg-gray-800">
                  &crarr;
                </kbd>{' '}
                open
              </span>
            </div>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {rankedRef.current.length > results.length
                ? `${results.length} of ${rankedRef.current.length}`
                : results.length}{' '}
              result{rankedRef.current.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
